// Set per environment via .env: REACT_APP_API_BASE_URL=http://localhost:8000
// to point at a local backend instead of editing this file.
const CORS_PROXY_URL = process.env.REACT_APP_CORS_PROXY_URL as string;

export const API_CONFIG = {
  getBaseUrl: () => {
    const backendUrl = process.env.REACT_APP_API_BASE_URL as string;
    const isLocal = backendUrl.includes('localhost') || backendUrl.includes('127.0.0.1') || backendUrl.includes('0.0.0.0');
    return isLocal ? backendUrl : `${CORS_PROXY_URL}/${backendUrl}`;
  }
};

export const OCR_CONFIG = {
  getBaseUrl: () => {
    const backendUrl = process.env.REACT_APP_OCR_BASE_URL as string;
    return `${CORS_PROXY_URL}/${backendUrl}`;
  }
};

export const API_ENDPOINTS = {
  ocrExtractBatch: () => `${OCR_CONFIG.getBaseUrl()}/api/ocr/extract/batch`,
  sendOtp: () => `${API_CONFIG.getBaseUrl()}/otp/send-whatsapp`,
  sendEmailOtp: () => `${API_CONFIG.getBaseUrl()}/email/send-otp`,
  verifyEmailOtp: () => `${API_CONFIG.getBaseUrl()}/email/verify-otp`,
  getUsers: () => `${API_CONFIG.getBaseUrl()}/users/customers`,
  getReferrals: () => `${API_CONFIG.getBaseUrl()}/users/referrals`,
  createUser: () => `${API_CONFIG.getBaseUrl()}/users/referral-signup`,
  getUserDetails: (mobile: string) => `${API_CONFIG.getBaseUrl()}/users/customers/${mobile}`,
  // Staff-only — looks up an Aadhaar number across both User (own KYC) and Nominee
  // records, so an already-on-file number can be reused instead of re-collected.
  searchKycByAadhar: (aadharNumber: string) => `${API_CONFIG.getBaseUrl()}/users/search/by-aadhar?aadhar_number=${encodeURIComponent(aadharNumber)}`,
  verifyUser: () => `${API_CONFIG.getBaseUrl()}/users/verify`,
  verifyKycFields: (mobile: string) => `${API_CONFIG.getBaseUrl()}/users/${mobile}/verify-kyc-fields`,
  kycAcknowledgment: (mobile: string) => `${API_CONFIG.getBaseUrl()}/users/${mobile}/kyc-acknowledgment`,
  nomineeCorrections: () => `${API_CONFIG.getBaseUrl()}/nominee-corrections`,
  farmvestSyncs: (mobile: string) => `${API_CONFIG.getBaseUrl()}/purchases/investors/${mobile}/farmvest-syncs`,
  cancelNomineeCorrection: (id: string) => `${API_CONFIG.getBaseUrl()}/nominee-corrections/${encodeURIComponent(id)}/cancel`,
  nomineeCorrectionByToken: (token: string) => `${API_CONFIG.getBaseUrl()}/nominee-corrections/by-token/${encodeURIComponent(token)}`,
  // Investor KYC / bank / unit-nominee flow (routers/kyc_flow.py). The investor page
  // authenticates with the link token; staff endpoints use the dashboard login.
  kycFlowByToken: (token: string) => `${API_CONFIG.getBaseUrl()}/kyc-flow/by-token/${encodeURIComponent(token)}`,
  kycFlowLinks: () => `${API_CONFIG.getBaseUrl()}/kyc-flow/links`,
  kycFlowOtpLink: () => `${API_CONFIG.getBaseUrl()}/kyc-flow/otp-link`,
  kycFlowStaff: (mobile: string) => `${API_CONFIG.getBaseUrl()}/kyc-flow/staff/${encodeURIComponent(mobile)}`,
  kycFlowCorrections: (mobile: string) => `${API_CONFIG.getBaseUrl()}/kyc-flow/${encodeURIComponent(mobile)}/corrections`,
  kycFlowConfig: () => `${API_CONFIG.getBaseUrl()}/kyc-flow/config`,
  kycFlowQueue: () => `${API_CONFIG.getBaseUrl()}/kyc-flow/queue`,
  kycFlowStaffEdit: (mobile: string) => `${API_CONFIG.getBaseUrl()}/kyc-flow/${encodeURIComponent(mobile)}/staff-edit`,
  kycFlowNomineeEdit: (mobile: string, unitId: string, nomineeId: string) => `${API_CONFIG.getBaseUrl()}/kyc-flow/${encodeURIComponent(mobile)}/units/${encodeURIComponent(unitId)}/nominees/${encodeURIComponent(nomineeId)}/staff-edit`,
  kycFlowStepMessage: (mobile: string) => `${API_CONFIG.getBaseUrl()}/kyc-flow/${encodeURIComponent(mobile)}/step-message`,
  kycFlowAckMessage: (mobile: string) => `${API_CONFIG.getBaseUrl()}/kyc-flow/${encodeURIComponent(mobile)}/ack-message`,
  kycFlowOrderNomineeMessage: (mobile: string, orderId: string) => `${API_CONFIG.getBaseUrl()}/kyc-flow/${encodeURIComponent(mobile)}/orders/${encodeURIComponent(orderId)}/nominee-message`,
  updateUser: (mobile: string) => `${API_CONFIG.getBaseUrl()}/users/${mobile}`,
  getProducts: () => `${API_CONFIG.getBaseUrl()}/products`,
  addProduct: () => `${API_CONFIG.getBaseUrl()}/products`,
  updateProduct: (id: string) => `${API_CONFIG.getBaseUrl()}/products/${id}`,
  deleteProduct: (id: string) => `${API_CONFIG.getBaseUrl()}/products/${id}`,
  health: () => `${API_CONFIG.getBaseUrl()}/health`,
  getPendingUnits: () => `${API_CONFIG.getBaseUrl()}/purchases/admin/units/pending`,
  approveUnit: () => `${API_CONFIG.getBaseUrl()}/purchases/admin/units/approve`,
  rejectUnit: () => `${API_CONFIG.getBaseUrl()}/purchases/admin/units/reject`,
  getAcfPendingEmis: () => `${API_CONFIG.getBaseUrl()}/purchases/admin/units/emis/pending`,
  approveAcfEmi: () => `${API_CONFIG.getBaseUrl()}/purchases/admin/units/approve-emi`,
  rejectAcfEmi: () => `${API_CONFIG.getBaseUrl()}/purchases/admin/units/reject-emi`,
  getEmiDetails: () => `${API_CONFIG.getBaseUrl()}/purchases/units/emi-details`,
  getInvoice: () => `${API_CONFIG.getBaseUrl()}/purchases/invoice`,
  getOrderDetails: (orderId: string) => `${API_CONFIG.getBaseUrl()}/purchases/units/order-details?orderId=${encodeURIComponent(orderId)}`,
  regenerateInvoice: () => `${API_CONFIG.getBaseUrl()}/purchases/invoice/regenerate`,
  uploadProductImage: (id: string) => `${API_CONFIG.getBaseUrl()}/products/${id}/images`,
  getOrderStatus: () => `${API_CONFIG.getBaseUrl()}/order-tracking/stages`,
  updateOrderStatus: () => `${API_CONFIG.getBaseUrl()}/order-tracking/update-status`,
  deactivateRequestOtp: () => `${API_CONFIG.getBaseUrl()}/users/deactivate/request-otp`,
  deactivateConfirm: () => `${API_CONFIG.getBaseUrl()}/users/deactivate/confirm`,
  requestReactivationOtp: () => `${API_CONFIG.getBaseUrl()}/users/reactivate/request-otp`,
  confirmReactivation: () => `${API_CONFIG.getBaseUrl()}/users/reactivate/confirm`,
  getFarms: () => `${API_CONFIG.getBaseUrl()}/farms/`,
  addFarm: () => `${API_CONFIG.getBaseUrl()}/farms/`,
  updateFarm: (id: string) => `${API_CONFIG.getBaseUrl()}/farms/${id}`,
  getSelfBenefits: () => `${API_CONFIG.getBaseUrl()}/self-benefits/`,
  updateSelfBenefit: (id: string) => `${API_CONFIG.getBaseUrl()}/self-benefits/${id}`,
  getReferralMilestones: () => `${API_CONFIG.getBaseUrl()}/referral-benefits/milestones`,
  updateReferralMilestone: (id: string) => `${API_CONFIG.getBaseUrl()}/referral-benefits/milestones/${encodeURIComponent(id)}`,
  getReferralConfig: () => `${API_CONFIG.getBaseUrl()}/referral-benefits/config`,
  updateReferralConfig: () => `${API_CONFIG.getBaseUrl()}/referral-benefits/config`,
  getRoleChangeRequests: () => `${API_CONFIG.getBaseUrl()}/users/role-change-requests`,
  actionRoleChangeRequest: (id: string) => `${API_CONFIG.getBaseUrl()}/users/role-change-requests/${encodeURIComponent(id)}`,
  getAchievedBenefits: () => `${API_CONFIG.getBaseUrl()}/self-benefits/admin/achieved`,
  getAchievedReferralMilestones: () => `${API_CONFIG.getBaseUrl()}/referral-benefits/admin/achieved-milestones`,
  generateCoupons: () => `${API_CONFIG.getBaseUrl()}/coupons/generate`,
  getCoupons: () => `${API_CONFIG.getBaseUrl()}/coupons/`,
  deleteCoupon: (code: string) => `${API_CONFIG.getBaseUrl()}/coupons/${encodeURIComponent(code)}`,
  assignCoupon: () => `${API_CONFIG.getBaseUrl()}/coupons/assign`,
  applyCoupon: () => `${API_CONFIG.getBaseUrl()}/coupons/apply`,
  removeCoupon: () => `${API_CONFIG.getBaseUrl()}/coupons/remove`,
  getOrderCoupon: (orderId: string) => `${API_CONFIG.getBaseUrl()}/coupons/order/${encodeURIComponent(orderId)}`,
  getUserCoupons: (mobile: string) => `${API_CONFIG.getBaseUrl()}/coupons/user/${encodeURIComponent(mobile)}`,
  couponHistory: () => `${API_CONFIG.getBaseUrl()}/coupons/history`,
  verifyOtp: () => `${API_CONFIG.getBaseUrl()}/otp/verify`,
  logout: () => `${API_CONFIG.getBaseUrl()}/otp/logout`,
  userSessions: (mobile: string) => `${API_CONFIG.getBaseUrl()}/users/${mobile}/sessions`,
  getProcurementRoster: () => `${API_CONFIG.getBaseUrl()}/order-tracking/procurement/roster`,
  searchProcurementRoster: () => `${API_CONFIG.getBaseUrl()}/order-tracking/procurement/roster/search`,
  procurementAction: () => `${API_CONFIG.getBaseUrl()}/order-tracking/procurement/action`,
  getAdmins: () => `${API_CONFIG.getBaseUrl()}/users/admins/list`,
  addAdmin: () => `${API_CONFIG.getBaseUrl()}/users/superadmin/add-user`,
  assignAdminToFarm: (farmId: string) => `${API_CONFIG.getBaseUrl()}/farms/${farmId}/admin`,
  getMarkets: () => `${API_CONFIG.getBaseUrl()}/markets/`,
  addMarket: () => `${API_CONFIG.getBaseUrl()}/markets/`,
  updateMarket: (id: string) => `${API_CONFIG.getBaseUrl()}/markets/${id}`,
  toggleMarketStatus: (id: string) => `${API_CONFIG.getBaseUrl()}/markets/${id}/status`,
  getAssetValues: () => `${API_CONFIG.getBaseUrl()}/purchases/asset-values`,
  updateAssetValues: () => `${API_CONFIG.getBaseUrl()}/purchases/asset-values`,
  getAssetValueHistory: () => `${API_CONFIG.getBaseUrl()}/purchases/asset-values/history`,
  getProduct: (id: string) => `${API_CONFIG.getBaseUrl()}/products/${id}`,
  getProductHistory: (id: string) => `${API_CONFIG.getBaseUrl()}/products/${id}/history`,
  editTransaction: () => `${API_CONFIG.getBaseUrl()}/purchases/admin/edit-transaction`,
  editOrderTransaction: (orderId: string) => `${API_CONFIG.getBaseUrl()}/purchases/orders/${encodeURIComponent(orderId)}/edit-transaction`,
  buyWithPayment: () => `${API_CONFIG.getBaseUrl()}/purchases/units/buy-with-payment`,
  setUnitNominee: () => `${API_CONFIG.getBaseUrl()}/purchases/units/nominee`,
  getUnitWiseNominees: (mobile: string) => `${API_CONFIG.getBaseUrl()}/purchases/investors/${mobile}/units/nominees`,
  setUnitWiseNominees: (unitId: string) => `${API_CONFIG.getBaseUrl()}/purchases/units/${unitId}/nominees`,
  addInvestorNominee: (mobile: string) => `${API_CONFIG.getBaseUrl()}/purchases/investors/${mobile}/nominees`,
  saveDraftNominees: (mobile: string) => `${API_CONFIG.getBaseUrl()}/purchases/investors/${mobile}/draft-nominees`,
  getDraftNominees: (mobile: string) => `${API_CONFIG.getBaseUrl()}/purchases/investors/${mobile}/draft-nominees`,
  updateNominee: (nomineeId: string) => `${API_CONFIG.getBaseUrl()}/purchases/units/nominee/${encodeURIComponent(nomineeId)}`,
  getOrderUnits: (orderId: string) => `${API_CONFIG.getBaseUrl()}/purchases/orders/${encodeURIComponent(orderId)}/units`,
  getInvestorNominees: (mobile: string) => `${API_CONFIG.getBaseUrl()}/purchases/investors/${encodeURIComponent(mobile)}/nominees`,
  syncFarmvest: (mobile: string) => `${API_CONFIG.getBaseUrl()}/purchases/investors/${encodeURIComponent(mobile)}/sync-farmvest`,
  checkTransactionReference: (value: string) => `${API_CONFIG.getBaseUrl()}/purchases/transactions/check-reference?value=${encodeURIComponent(value)}`,
  getUserOrders: (mobile: string) => `${API_CONFIG.getBaseUrl()}/purchases/units/${mobile}`,
  confirmPayment: () => `${API_CONFIG.getBaseUrl()}/purchases/units/confirm-payment`,
  generateReceipt: () => `${API_CONFIG.getBaseUrl()}/purchases/units/generate-receipt`,
  sendReceipt: () => `${API_CONFIG.getBaseUrl()}/purchases/units/send-receipt`,
  getCoinTransaction: (mobile: string) => `${API_CONFIG.getBaseUrl()}/users/coinTransaction/${mobile}`,
  getLockedCoinOrders: (mobile: string) =>
    `${API_CONFIG.getBaseUrl()}/users/coinTransaction/${encodeURIComponent(mobile)}/locked-orders`,
  releaseLockedCoins: () => `${API_CONFIG.getBaseUrl()}/users/coinTransaction/release`,
  getMoneyTransaction: (mobile: string) => `${API_CONFIG.getBaseUrl()}/users/moneyTransaction/${mobile}`,
  validateOrderSession: (token: string, orderId: string) =>
    `${API_CONFIG.getBaseUrl()}/purchases/session/validate?token=${encodeURIComponent(token)}&orderId=${encodeURIComponent(orderId)}`,
  deleteOrder: (orderId: string) => `${API_CONFIG.getBaseUrl()}/purchases/admin/units/${encodeURIComponent(orderId)}`,
  deleteRefundOrder: (orderId: string) => `${API_CONFIG.getBaseUrl()}/purchases/admin/units/${encodeURIComponent(orderId)}/delete-refund`,
  // Read-only breakdown of what a Delete & Refund would move (money, buyer
  // coins, referral clawbacks). No OTP — safe to call whenever the modal opens.
  refundPreview: (orderId: string) => `${API_CONFIG.getBaseUrl()}/purchases/admin/units/${encodeURIComponent(orderId)}/refund-preview`,
  bulkDeleteOrders: () => `${API_CONFIG.getBaseUrl()}/purchases/admin/units/bulk-delete`,
  sendOrderForApproval: (orderId: string) => `${API_CONFIG.getBaseUrl()}/purchases/orders/${encodeURIComponent(orderId)}/send-for-approval`,
  getBanks: () => `${API_CONFIG.getBaseUrl()}/banks/`,
  // Cashier roster (backend routers/cashiers.py). The list that the cashier
  // name/phone on a CASH payment is picked from -- same relationship getBanks
  // has with a transaction's bankName. Roster cashiers never log in.
  getCashiers: (params?: { search?: string; activeOnly?: boolean }) => {
    const q = new URLSearchParams();
    if (params?.search) q.set('search', params.search);
    if (params?.activeOnly) q.set('active_only', 'true');
    const qs = q.toString();
    return `${API_CONFIG.getBaseUrl()}/cashiers${qs ? `?${qs}` : ''}`;
  },
  addCashier: () => `${API_CONFIG.getBaseUrl()}/cashiers`,
  updateCashier: (cashierId: string) => `${API_CONFIG.getBaseUrl()}/cashiers/${encodeURIComponent(cashierId)}`,
  deactivateCashier: (cashierId: string) => `${API_CONFIG.getBaseUrl()}/cashiers/${encodeURIComponent(cashierId)}`,
  getAnalyticsSummary: (year?: number) =>
    `${API_CONFIG.getBaseUrl()}/purchases/analytics/summary${year ? `?year=${year}` : ''}`,
  listStaff: () => `${API_CONFIG.getBaseUrl()}/users/staff`,
  addStaff: () => `${API_CONFIG.getBaseUrl()}/users/staff`,
  setScreenAccess: (mobile: string) => `${API_CONFIG.getBaseUrl()}/users/${encodeURIComponent(mobile)}/screen-access`,
  changeStaffPosition: (mobile: string) =>
    `${API_CONFIG.getBaseUrl()}/users/staff/${encodeURIComponent(mobile)}/position`,
  addStaffTeam: (mobile: string) =>
    `${API_CONFIG.getBaseUrl()}/users/staff/${encodeURIComponent(mobile)}/team`,
  revokeStaffRole: (mobile: string, role: string) =>
    `${API_CONFIG.getBaseUrl()}/roles/staff/${encodeURIComponent(mobile)}/${encodeURIComponent(role)}`,

  // Marketing
  marketingStaffList: (scope?: 'team') => `${API_CONFIG.getBaseUrl()}/marketing/staff${scope ? `?scope=${scope}` : ''}`,
  marketingOnboardConfig: () => `${API_CONFIG.getBaseUrl()}/marketing/onboard-config`,
  marketingResolveReferrer: (mobile: string) => `${API_CONFIG.getBaseUrl()}/marketing/resolve-referrer/${encodeURIComponent(mobile)}`,
  marketingFarmStaffList: () => `${API_CONFIG.getBaseUrl()}/marketing/farm-staff`,
  marketingOrdersList: (params?: Record<string, string | number | boolean | undefined>) => {
    const qs = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== '') qs.append(k, String(v));
    });
    const q = qs.toString();
    return `${API_CONFIG.getBaseUrl()}/marketing/orders${q ? `?${q}` : ''}`;
  },
  // Tiles and executive-wise table only — /marketing/orders without the rows.
  marketingOrdersSummary: (params?: Record<string, string | number | boolean | undefined>) => {
    const qs = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== '') qs.append(k, String(v));
    });
    const q = qs.toString();
    return `${API_CONFIG.getBaseUrl()}/marketing/orders/summary${q ? `?${q}` : ''}`;
  },
  // The orders one executive_incentives row counts (execMobile + month).
  marketingExecutiveOrders: (params?: Record<string, string | number | boolean | undefined>) => {
    const qs = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== '') qs.append(k, String(v));
    });
    const q = qs.toString();
    return `${API_CONFIG.getBaseUrl()}/marketing/orders/by-executive${q ? `?${q}` : ''}`;
  },
  // Farm Orders: tiles + farm-executive table, and one farm executive-month's orders.
  marketingFarmOrdersSummary: (params?: Record<string, string | number | boolean | undefined>) => {
    const qs = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== '') qs.append(k, String(v));
    });
    const q = qs.toString();
    return `${API_CONFIG.getBaseUrl()}/marketing/farm-orders/summary${q ? `?${q}` : ''}`;
  },
  marketingFarmExecutiveOrders: (params?: Record<string, string | number | boolean | undefined>) => {
    const qs = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== '') qs.append(k, String(v));
    });
    const q = qs.toString();
    return `${API_CONFIG.getBaseUrl()}/marketing/farm-orders/by-executive${q ? `?${q}` : ''}`;
  },
  marketingPendingApprovals: (params?: Record<string, string | boolean | undefined>) => {
    const qs = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== '') qs.append(k, String(v));
    });
    const q = qs.toString();
    return `${API_CONFIG.getBaseUrl()}/marketing/incentive-approvals/pending${q ? `?${q}` : ''}`;
  },
  marketingApproveIncentive: () => `${API_CONFIG.getBaseUrl()}/marketing/incentive-approvals`,
  marketingApproveIncentivesBulk: () => `${API_CONFIG.getBaseUrl()}/marketing/incentive-approvals/bulk`,
  marketingApproveFarmIncentive: () => `${API_CONFIG.getBaseUrl()}/marketing/farm-incentive-approvals`,
  marketingApproveFarmIncentivesBulk: () => `${API_CONFIG.getBaseUrl()}/marketing/farm-incentive-approvals/bulk`,
  marketingSummary: (params?: Record<string, string | number | boolean | undefined>) => {
    const qs = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== '') qs.append(k, String(v));
    });
    const q = qs.toString();
    return `${API_CONFIG.getBaseUrl()}/marketing/summary${q ? `?${q}` : ''}`;
  },
  // Edit the overall monthly target's window (start/end date) — shared, so
  // this changes it for every team member linked to that month at once.
  targetsEditWindow: () => `${API_CONFIG.getBaseUrl()}/targets`,
  // Edit one person's target for a month — overrides the shared monthly
  // default just for them, leaving everyone else's target untouched.
  targetsEditPerson: (mobile: string) => `${API_CONFIG.getBaseUrl()}/targets/${mobile}`,
  targetsOverview: (params?: Record<string, string | number | boolean | undefined>) => {
    const qs = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== '') qs.append(k, String(v));
    });
    const q = qs.toString();
    return `${API_CONFIG.getBaseUrl()}/targets${q ? `?${q}` : ''}`;
  },
  leadsOverview: (params?: Record<string, string | number | boolean | undefined>) => {
    const qs = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== '') qs.append(k, String(v));
    });
    const q = qs.toString();
    return `${API_CONFIG.getBaseUrl()}/leads${q ? `?${q}` : ''}`;
  },
  callsList: (params?: Record<string, string | number | boolean | undefined>) => {
    const qs = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== '') qs.append(k, String(v));
    });
    const q = qs.toString();
    return `${API_CONFIG.getBaseUrl()}/calls${q ? `?${q}` : ''}`;
  },
  marketingLeaderboard: (month?: string) => `${API_CONFIG.getBaseUrl()}/marketing/leaderboard${month ? `?month=${encodeURIComponent(month)}` : ''}`,
  marketingLeadConversion: (params?: { month?: string; from_date?: string; end_date?: string; person?: string }) => {
    const q = new URLSearchParams();
    if (params?.month) q.append('month', params.month);
    if (params?.from_date) q.append('from_date', params.from_date);
    if (params?.end_date) q.append('end_date', params.end_date);
    if (params?.person) q.append('person', params.person);
    const qs = q.toString();
    return `${API_CONFIG.getBaseUrl()}/marketing/lead-conversion${qs ? `?${qs}` : ''}`;
  },
  marketingTopPerformer: (month?: string) => `${API_CONFIG.getBaseUrl()}/marketing/leaderboard/top-performer${month ? `?month=${encodeURIComponent(month)}` : ''}`,
  farmTopPerformer: (month?: string) => `${API_CONFIG.getBaseUrl()}/farm-incentives/leaderboard/top-performer${month ? `?month=${encodeURIComponent(month)}` : ''}`,
  marketingSalesReport: (params?: { month?: string; year?: number; from_date?: string; to_date?: string }) => {
    const q = new URLSearchParams();
    if (params?.year) q.append('year', String(params.year));
    if (params?.month) q.append('month', params.month);
    if (params?.from_date) q.append('from_date', params.from_date);
    if (params?.to_date) q.append('to_date', params.to_date);
    const qs = q.toString();
    return `${API_CONFIG.getBaseUrl()}/marketing/sales-report${qs ? `?${qs}` : ''}`;
  },
  marketingOrderLeadBy: (orderId: string) => `${API_CONFIG.getBaseUrl()}/marketing/orders/${encodeURIComponent(orderId)}/lead-by`,
  incentivePlans: (params?: { period?: string; status?: string; month_year?: string }) => {
    const qs = new URLSearchParams();
    if (params?.period) qs.append('period', params.period);
    if (params?.status) qs.append('status', params.status);
    if (params?.month_year) qs.append('month_year', params.month_year);
    const q = qs.toString();
    return `${API_CONFIG.getBaseUrl()}/incentive-plans${q ? `?${q}` : ''}`;
  },
  incentivePlan: (planId: string) => `${API_CONFIG.getBaseUrl()}/incentive-plans/${encodeURIComponent(planId)}`,
  incentivePlanHistory: (planId: string) => `${API_CONFIG.getBaseUrl()}/incentive-plans/${encodeURIComponent(planId)}/history`,

  // Farm staff incentives — separate from the marketing plans above. Farm
  // executives are paid on their own progressive ladder, stored under its own
  // node labels, so none of the /incentive-plans endpoints answer for them.
  farmIncentivePlans: (params?: { month_year?: string }) => {
    const qs = new URLSearchParams();
    if (params?.month_year) qs.append('month_year', params.month_year);
    const q = qs.toString();
    return `${API_CONFIG.getBaseUrl()}/farm-incentive-plans${q ? `?${q}` : ''}`;
  },
  farmIncentivePlan: (planId: string) =>
    `${API_CONFIG.getBaseUrl()}/farm-incentive-plans/${encodeURIComponent(planId)}`,
  farmIncentivePlanHistory: (planId: string) =>
    `${API_CONFIG.getBaseUrl()}/farm-incentive-plans/${encodeURIComponent(planId)}/history`,
  farmIncentivesMy: (params?: { mobile?: string; month_year?: string }) => {
    const qs = new URLSearchParams();
    if (params?.mobile) qs.append('mobile', params.mobile);
    if (params?.month_year) qs.append('month_year', params.month_year);
    const q = qs.toString();
    return `${API_CONFIG.getBaseUrl()}/farm-incentives/my${q ? `?${q}` : ''}`;
  },
  farmLeaderboard: (params?: { month?: string }) => {
    const qs = new URLSearchParams();
    if (params?.month) qs.append('month', params.month);
    const q = qs.toString();
    return `${API_CONFIG.getBaseUrl()}/farm-incentives/leaderboard${q ? `?${q}` : ''}`;
  },

  // Leads
  leadsVisits: (params?: Record<string, string | number | boolean | undefined>) => {
    const qs = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== '') qs.append(k, String(v));
    });
    const q = qs.toString();
    return `${API_CONFIG.getBaseUrl()}/leads/visits${q ? `?${q}` : ''}`;
  },
  // Flat guest list across matching visits (GET /leads/visits/guests) — the
  // Guests tab's own endpoint; the backend flattens, not the frontend.
  leadsVisitGuests: (params?: Record<string, string | number | boolean | undefined>) => {
    const qs = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== '') qs.append(k, String(v));
    });
    const q = qs.toString();
    return `${API_CONFIG.getBaseUrl()}/leads/visits/guests${q ? `?${q}` : ''}`;
  },
  // Visitors someone actually checked in, with timings, executives and guests
  // (GET /leads/checked-in-visits). Staff-only, org-wide.
  leadsCheckedInVisits: (params?: Record<string, string | number | boolean | undefined>) => {
    const qs = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== '') qs.append(k, String(v));
    });
    const q = qs.toString();
    return `${API_CONFIG.getBaseUrl()}/leads/checked-in-visits${q ? `?${q}` : ''}`;
  },
  leadsUpload: () => `${API_CONFIG.getBaseUrl()}/leads/upload`,
  leadsBase: () => `${API_CONFIG.getBaseUrl()}/leads`,
  leadsAssign: () => `${API_CONFIG.getBaseUrl()}/leads/assign`,
  leadsDetail: (leadId: string) => `${API_CONFIG.getBaseUrl()}/leads/${encodeURIComponent(leadId)}`,
  leadsStatus: (leadId: string) => `${API_CONFIG.getBaseUrl()}/leads/${encodeURIComponent(leadId)}/status`,

  // Departments / team
  myTeam: (departmentHeadId: string) => `${API_CONFIG.getBaseUrl()}/departments/my-team?department_head_id=${encodeURIComponent(departmentHeadId)}`,
  listDepartments: () => `${API_CONFIG.getBaseUrl()}/departments`,
  getDepartmentMembers: (deptId: string, role?: string) =>
    `${API_CONFIG.getBaseUrl()}/departments/${encodeURIComponent(deptId)}${role ? `?role=${encodeURIComponent(role)}` : ''}`,
  createDepartment: () => `${API_CONFIG.getBaseUrl()}/departments`,
  createDepartmentRole: (deptId: string) => `${API_CONFIG.getBaseUrl()}/departments/${encodeURIComponent(deptId)}/roles`,
  assignDepartmentHead: (deptId: string) => `${API_CONFIG.getBaseUrl()}/departments/${encodeURIComponent(deptId)}/head`,
  updateDepartmentRole: (deptId: string, roleName: string) =>
    `${API_CONFIG.getBaseUrl()}/departments/${encodeURIComponent(deptId)}/roles/${encodeURIComponent(roleName)}`,
  deleteDepartmentRole: (deptId: string, roleName: string) =>
    `${API_CONFIG.getBaseUrl()}/departments/${encodeURIComponent(deptId)}/roles/${encodeURIComponent(roleName)}`,
  deleteDepartment: (deptId: string, force?: boolean) =>
    `${API_CONFIG.getBaseUrl()}/departments/${encodeURIComponent(deptId)}${force ? '?force=true' : ''}`,

  // Farm Management
  farmManagementRoster: () => `${API_CONFIG.getBaseUrl()}/farm-management/roster`,
  farmManagementSummary: () => `${API_CONFIG.getBaseUrl()}/farm-management/procurement-summary`,
  farmManagementAssign: () => `${API_CONFIG.getBaseUrl()}/farm-management/assign`,
  farmManagementMyAssistants: () => `${API_CONFIG.getBaseUrl()}/farm-management/my-assistants`,
  farmManagementSelfAssign: () => `${API_CONFIG.getBaseUrl()}/farm-management/self-assign`,
  farmManagementUnassign: () => `${API_CONFIG.getBaseUrl()}/farm-management/unassign`,
  farmManagementBuffalo: (buffaloId: string) => `${API_CONFIG.getBaseUrl()}/farm-management/buffalo/${encodeURIComponent(buffaloId)}`,
  farmManagementBuffaloProcured: (buffaloId: string) => `${API_CONFIG.getBaseUrl()}/farm-management/buffalo/${encodeURIComponent(buffaloId)}/procured`,
  farmManagementBuffaloPhotos: (buffaloId: string) => `${API_CONFIG.getBaseUrl()}/farm-management/buffalo/${encodeURIComponent(buffaloId)}/photos`,
  farmManagementProcurementBuffaloes: () => `${API_CONFIG.getBaseUrl()}/farm-management/procurement/buffaloes`,
  farmManagementProcurementMarkets: () => `${API_CONFIG.getBaseUrl()}/farm-management/procurement/markets`,
  farmManagementProcurementBuyers: () => `${API_CONFIG.getBaseUrl()}/farm-management/procurement/buyers`,
  farmManagementBuyerStatus: (buyerId: string) =>
    `${API_CONFIG.getBaseUrl()}/farm-management/procurement/buyers/${encodeURIComponent(buyerId)}/status`,
  farmManagementMarketStatus: (marketId: string) =>
    `${API_CONFIG.getBaseUrl()}/farm-management/procurement/markets/${encodeURIComponent(marketId)}/status`,
  farmManagementProcurementBuffaloDetail: (buffaloId: string) => `${API_CONFIG.getBaseUrl()}/farm-management/procurement/buffaloes/${encodeURIComponent(buffaloId)}`,
  farmManagementAdvanceBuffaloes: () => `${API_CONFIG.getBaseUrl()}/farm-management/procurement/buffaloes/advance`,
  farmManagementTodayTarget: () => `${API_CONFIG.getBaseUrl()}/farm-management/procurement/today-target`,
  farmManagementEditHalt: (haltId: string) => `${API_CONFIG.getBaseUrl()}/farm-management/procurement/halts/${encodeURIComponent(haltId)}`,
  farmManagementProcurementDeaths: () => `${API_CONFIG.getBaseUrl()}/farm-management/procurement/deaths`,
  farmManagementCalf: (calfId: string) => `${API_CONFIG.getBaseUrl()}/farm-management/calf/${encodeURIComponent(calfId)}`,
  farmManagementCalfPhotos: (calfId: string) => `${API_CONFIG.getBaseUrl()}/farm-management/calf/${encodeURIComponent(calfId)}/photos`,
  farmManagementProcurementBuffaloDeath: (buffaloId: string) => `${API_CONFIG.getBaseUrl()}/farm-management/procurement/buffaloes/${encodeURIComponent(buffaloId)}/death`,
  farmManagementProcurementCalfDeath: (calfId: string) => `${API_CONFIG.getBaseUrl()}/farm-management/procurement/calves/${encodeURIComponent(calfId)}/death`,
  farmManagementProcurementOrphanCalves: () => `${API_CONFIG.getBaseUrl()}/farm-management/procurement/orphan-calves`,

  // Calculator Settings
  getCalculatorSettings: () => `${API_CONFIG.getBaseUrl()}/calculator-settings/active`,
  getCalculatorSettingsByVersion: (version: number) => `${API_CONFIG.getBaseUrl()}/calculator-settings/model/${version}`,
  getCalculatorSettingsHistory: () => `${API_CONFIG.getBaseUrl()}/calculator-settings/history`,
  requestCalculatorSettingsOtp: () => `${API_CONFIG.getBaseUrl()}/calculator-settings/request-otp`,
  updateCalculatorSettings: () => `${API_CONFIG.getBaseUrl()}/calculator-settings/update`,
  activateCalculatorSettingsModel: () => `${API_CONFIG.getBaseUrl()}/calculator-settings/activate`,
  getCurrentCalculatorSettings: () => `${API_CONFIG.getBaseUrl()}/calculator-settings/current`,

  // Unit Transfers
  unitTransferDetails: (unitNumber: string) => `${API_CONFIG.getBaseUrl()}/unit-transfers/units/${encodeURIComponent(unitNumber)}`,
};

